import { Box, ChipGroup, Flex, Label, Text, type GridColDef } from '@shared/components';
import { formatDateTime } from '@shared/utils';
import { UserTypeAvatar } from '@user-management/components';
import {
  USER_COLOR_STATUS,
  USER_STATUS_TEXT,
  type UserStatus,
} from '@user-management/user-details';

import { MoreActionButton } from '../more-action-button';

interface User {
  id: string;
  firstname: string;
  lastname: string;
  email: string | null;
  appRoles: string[] | null;
  status: UserStatus;
}

export const columns: GridColDef<User>[] = [
  {
    // NOTE: This is named as firstname to match the field's name we wanted to sort by
    field: 'firstname',
    headerName: 'Name',
    renderCell: ({ row }) => {
      const { firstname, lastname, email } = row;
      const fullName = `${firstname} ${lastname}`;
      return (
        <Flex sx={{ gap: (theme) => theme.spacing(3) }}>
          <UserTypeAvatar fullName={fullName} email={email ?? ''} />
          <Box>
            <Text variant='body1Semibold'>{fullName}</Text>
            <Text variant='body2' sx={{ color: (theme) => theme.palette.text.secondary }}>
              {email}
            </Text>
          </Box>
        </Flex>
      );
    },
    flex: 1,
  },
  {
    field: 'appRoles',
    headerName: 'Role',
    sortable: false,
    width: 240,
    renderCell: (params) => {
      if (!params.row.appRoles) return null;
      return <ChipGroup labels={params.row.appRoles} limit={2} />;
    },
  },
  {
    field: 'lastLoginAt',
    headerName: 'Last Login',
    type: 'string',
    width: 160,
    valueFormatter: (params) => {
      if (!params.value) {
        return '-';
      }
      return formatDateTime(params.value);
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 160,
    renderCell: ({ row }) => {
      if (!row.status) return null;

      const status = row.status;
      return (
        <Label
          sx={{ px: (theme) => theme.spacing(3) }}
          color={USER_COLOR_STATUS[status]}
          variant='outlined'
        >
          {USER_STATUS_TEXT[status]}
        </Label>
      );
    },
  },
  {
    field: 'moreAction',
    headerName: '',
    width: 68,
    renderCell: (params) => {
      const userId = params.row.id ?? '';
      const status = params.row.status ?? null;
      return <MoreActionButton userId={userId} userStatus={status} />;
    },
    sortable: false,
  },
];
